import { CommandTooltip } from '@modules/common/components/RichTextEditor/CommandTooltip';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TitleIcon from '@mui/icons-material/Title';
import {
  Box,
  ButtonProps,
  IconButton,
  MenuItem,
  Stack,
  Theme,
  Typography,
  alpha,
} from '@mui/material';
import Menu from '@mui/material/Menu';
import { useCurrentEditor } from '@tiptap/react';
import { useState } from 'react';

interface TypographyFormattingButtonProps extends ButtonProps {}

export function TypographyFormattingButton({
  ...buttonProps
}: TypographyFormattingButtonProps) {
  const { editor } = useCurrentEditor();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  if (!editor) {
    return null;
  }

  return (
    <Box>
      <IconButton
        {...buttonProps}
        onClick={handleClickListItem}
        sx={{
          ...buttonProps.sx,
          color: isOpen ? 'primary.main' : 'text.secondary',
          backgroundColor: isOpen
            ? (theme: Theme) => alpha(theme.palette.primary.main, 0.1)
            : undefined,
        }}
      >
        <Stack direction="row" alignItems="center">
          <TitleIcon fontSize="small" />
          <KeyboardArrowDownIcon fontSize="small" />
        </Stack>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={closeMenu}
        MenuListProps={{
          'aria-labelledby': 'lock-button',
          role: 'listbox',
        }}
      >
        <CommandTooltip placement="right" shortcut={['$mod', 'Alt', '0']}>
          <MenuItem
            key={'text'}
            selected={editor.isActive('paragraph')}
            onClick={() => {
              editor.chain().focus().setParagraph().run();
              editor.commands.focus('start');
              closeMenu();
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <TitleIcon fontSize="small" />
              <Typography>Text</Typography>
            </Stack>
          </MenuItem>
        </CommandTooltip>

        <CommandTooltip placement="right" shortcut={['$mod', 'Alt', '2']}>
          <MenuItem
            key={'title'}
            selected={editor.isActive('heading', { level: 4 })}
            onClick={() => {
              editor.chain().focus().toggleHeading({ level: 4 }).run();
              editor.commands.focus('start');
              closeMenu();
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <TitleIcon fontSize="small" />
              <Typography>Title</Typography>
            </Stack>
          </MenuItem>
        </CommandTooltip>

        <CommandTooltip placement="right" shortcut={['$mod', 'Alt', '3']}>
          <MenuItem
            key={'subtitle'}
            selected={editor.isActive('heading', { level: 5 })}
            onClick={() => {
              editor.chain().focus().toggleHeading({ level: 5 }).run();
              editor.commands.focus('start');
              closeMenu();
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <TitleIcon fontSize="small" />
              <Typography>Subtitle</Typography>
            </Stack>
          </MenuItem>
        </CommandTooltip>
      </Menu>
    </Box>
  );
}
